/**
 * Note Class
 *
 * Represents a single note in the gameplay
 */

export interface NoteData {
  time: number;      // When the note should be hit (in milliseconds)
  lane: number;      // Which lane (0-3 for 4-lane mode)
  type: 'tap';       // Note type (only 'tap' for MVP)
}

export class Note {
  public readonly time: number;
  public readonly lane: number;
  public readonly type: string;

  public position: number = 0; // 0 = spawned, 1 = at judgment line
  public hit: boolean = false;
  public missed: boolean = false;

  constructor(data: NoteData) {
    this.time = data.time;
    this.lane = data.lane;
    this.type = data.type;
  }

  /**
   * Update note position based on current time
   * @param currentTime Current game time in milliseconds
   * @param approachTime How long before hit time the note appears
   */
  update(currentTime: number, approachTime: number): void {
    // Calculate how far the note should be along its path
    // position = 0 when spawned, 1 when at judgment line
    const timeUntilHit = this.time - currentTime;
    this.position = 1 - (timeUntilHit / approachTime);
  }

  /**
   * Check if note should be despawned (hit, missed, or off screen)
   */
  shouldDespawn(currentTime: number, _missWindow: number = 180): boolean {
    if (this.hit || this.missed) return true;

    // Don't auto-despawn here - let JudgmentSystem handle misses
    // Just despawn if it's way past (500ms = half second)
    const timePassed = currentTime - this.time;
    if (timePassed > 500) {
      return true;
    }

    return false;
  }

  /**
   * Mark note as hit
   */
  markHit(): void {
    this.hit = true;
  }

  /**
   * Mark note as missed
   */
  markMissed(): void {
    this.missed = true;
  }

  /**
   * Check if note is in hit window
   */
  isInHitWindow(currentTime: number, windowMs: number): boolean {
    const diff = Math.abs(currentTime - this.time);
    return diff <= windowMs;
  }

  /**
   * Get timing difference (how early/late the input was)
   * Negative = early, Positive = late
   */
  getTimingDifference(inputTime: number): number {
    return inputTime - this.time;
  }
}
